/**
 * Rate limiting for ludicrous-gateway
 * Implements per-provider rate limiting with token bucket algorithm
 */

import { getLogger, type Logger } from "../utils/logger.js";

/**
 * Rate limiter configuration
 */
export interface RateLimiterConfig {
  /** Maximum requests per window */
  maxRequests: number;
  /** Window size in milliseconds */
  windowMs: number;
  /** Whether to queue requests when rate limited */
  queueEnabled?: boolean;
  /** Maximum queue size */
  maxQueueSize?: number;
}

/**
 * Rate limiter statistics
 */
export interface RateLimiterStats {
  requests: number;
  windowStart: Date;
  remaining: number;
  queueSize: number;
  rejectedCount: number;
}

/**
 * Result of a rate limit check
 */
export interface RateLimitResult {
  allowed: boolean;
  remaining: number;
  resetTime: Date;
  retryAfterMs?: number;
}

/**
 * Sliding window rate limiter
 */
export class RateLimiter {
  readonly name: string;
  private requests: number[] = [];
  private rejectedCount: number = 0;
  private config: Required<RateLimiterConfig>;
  private logger: Logger;

  constructor(
    name: string,
    config: RateLimiterConfig
  ) {
    this.name = name;
    this.config = {
      maxRequests: config.maxRequests,
      windowMs: config.windowMs,
      queueEnabled: config.queueEnabled ?? false,
      maxQueueSize: config.maxQueueSize ?? 100,
    };
    this.logger = getLogger().child({ rateLimiter: name });
  }

  /**
   * Check if a request is allowed
   */
  checkLimit(): RateLimitResult {
    this.pruneOldRequests();

    const now = Date.now();
    const resetTime = new Date(now + this.config.windowMs);

    if (this.requests.length >= this.config.maxRequests) {
      this.rejectedCount++;
      const oldestRequest = this.requests[0] ?? now;
      const retryAfterMs = oldestRequest + this.config.windowMs - now;

      return {
        allowed: false,
        remaining: 0,
        resetTime,
        retryAfterMs: Math.max(0, retryAfterMs),
      };
    }

    return {
      allowed: true,
      remaining: this.config.maxRequests - this.requests.length - 1,
      resetTime,
    };
  }

  /**
   * Record a request (call after checkLimit returns allowed: true)
   */
  recordRequest(): void {
    this.requests.push(Date.now());
  }

  /**
   * Try to make a request (combines check and record)
   */
  tryRequest(): RateLimitResult {
    const result = this.checkLimit();
    if (result.allowed) {
      this.recordRequest();
    }
    return result;
  }

  /**
   * Get current statistics
   */
  getStats(): RateLimiterStats {
    this.pruneOldRequests();

    const windowStart = new Date(Date.now() - this.config.windowMs);

    return {
      requests: this.requests.length,
      windowStart,
      remaining: Math.max(0, this.config.maxRequests - this.requests.length),
      queueSize: 0, // Queue not implemented in this version
      rejectedCount: this.rejectedCount,
    };
  }

  /**
   * Reset the rate limiter
   */
  reset(): void {
    this.requests = [];
    this.rejectedCount = 0;
    this.logger.debug("Rate limiter reset");
  }

  /**
   * Remove requests outside the current window
   */
  private pruneOldRequests(): void {
    const windowStart = Date.now() - this.config.windowMs;
    this.requests = this.requests.filter((t) => t > windowStart);
  }
}

/**
 * Rate limiter manager for multiple providers
 */
export class RateLimiterManager {
  private limiters: Map<string, RateLimiter> = new Map();
  private defaultConfig: RateLimiterConfig;

  constructor(defaultConfig: RateLimiterConfig) {
    this.defaultConfig = defaultConfig;
  }

  /**
   * Get or create a rate limiter for a provider
   */
  getLimiter(providerName: string): RateLimiter {
    let limiter = this.limiters.get(providerName);
    if (!limiter) {
      limiter = new RateLimiter(providerName, this.defaultConfig);
      this.limiters.set(providerName, limiter);
    }
    return limiter;
  }

  /**
   * Set custom config for a specific provider
   */
  setProviderConfig(providerName: string, config: RateLimiterConfig): void {
    const limiter = new RateLimiter(providerName, config);
    this.limiters.set(providerName, limiter);
  }

  /**
   * Check rate limit for a provider
   */
  checkLimit(providerName: string): RateLimitResult {
    return this.getLimiter(providerName).checkLimit();
  }

  /**
   * Try a request for a provider
   */
  tryRequest(providerName: string): RateLimitResult {
    return this.getLimiter(providerName).tryRequest();
  }

  /**
   * Get all rate limiter stats
   */
  getAllStats(): Map<string, RateLimiterStats> {
    const stats = new Map<string, RateLimiterStats>();
    for (const [name, limiter] of this.limiters) {
      stats.set(name, limiter.getStats());
    }
    return stats;
  }

  /**
   * Reset all rate limiters
   */
  resetAll(): void {
    for (const limiter of this.limiters.values()) {
      limiter.reset();
    }
  }
}

/**
 * Default rate limit configurations by provider type
 */
export const defaultRateLimits: Record<string, RateLimiterConfig> = {
  anthropic: {
    maxRequests: 100,
    windowMs: 60000, // 1 minute
  },
  zai: {
    maxRequests: 60,
    windowMs: 60000,
  },
  ollama: {
    maxRequests: 1000, // Local, so high limit
    windowMs: 60000,
  },
  kimi: {
    maxRequests: 50,
    windowMs: 60000,
  },
};
